import {
	CmVisitScheduleOption,
	TreatmentConfigOptions,
} from "../../../domain/treatmentPlan.interface";
import {
	CUT_DAY_NAMES,
	deriveCutDays,
	nextOptionNumber,
} from "../visitSchedule/cutDays";

/** A visit schedule the assistant is proposing, named by its days. */
export interface ProposedVisitSchedule {
	days: string;
	display?: string;
}

/**
 * Option lists the assistant proposes adding to the plan.
 *
 * A template can only reference values the plan offers, so an assistant that
 * could only write templates was stuck the moment the plan was empty. It
 * proposes the lists too — but proposes: nothing here is saved until the
 * clinician applies it and saves the plan.
 */
export interface ProposedOptions {
	duration?: (string | number)[];
	visitsPerPeriod?: (string | number)[];
	minHoursBetweenVisits?: (string | number)[];
	behavior?: string[];
	visitSchedules?: ProposedVisitSchedule[];
}

export interface OptionMergeResult {
	options: TreatmentConfigOptions;
	/** What applying this would add, in words, for the confirmation list. */
	additions: string[];
}

/**
 * Day names as the plan stores them: "Sun".."Sat", in week order.
 *
 * Compared normalised because "Mon,Thu", "mon, thu" and "Thu,Mon" are the same
 * schedule, and a near-duplicate option is worse than none — a template can
 * then point at whichever copy the agent happened to name.
 */
export const normaliseDays = (days: string): string => {
	const byWeekOrder = (a: string, b: string): number =>
		CUT_DAY_NAMES.indexOf(a as (typeof CUT_DAY_NAMES)[number]) -
		CUT_DAY_NAMES.indexOf(b as (typeof CUT_DAY_NAMES)[number]);

	return (days ?? "")
		.split(",")
		.map((day) => day.trim().toLowerCase())
		.filter(Boolean)
		.map((day) => day.charAt(0).toUpperCase() + day.slice(1, 3))
		.filter((day) =>
			CUT_DAY_NAMES.includes(day as (typeof CUT_DAY_NAMES)[number]),
		)
		.sort(byWeekOrder)
		.join(",");
};

const mergeScalarList = (
	existing: string[] | undefined,
	proposed: (string | number)[] | undefined,
	label: string,
	additions: string[],
): string[] => {
	const current = existing ?? [];
	if (!proposed?.length) return current;

	const merged = [...current];
	for (const value of proposed) {
		const asString = String(value).trim();
		if (!asString || merged.includes(asString)) continue;
		merged.push(asString);
		additions.push(`${label}: ${asString}`);
	}
	return merged;
};

/**
 * Fold the assistant's proposed options into the plan's.
 *
 * Additive and idempotent: a value the plan already has is left alone, so
 * re-applying the same draft twice does not duplicate anything. Nothing is
 * removed — the assistant can offer the clinician more to choose from, never
 * take away what is already in use by another template.
 */
export const mergeDraftOptions = (
	proposed: ProposedOptions | undefined,
	planOptions: TreatmentConfigOptions | undefined,
): OptionMergeResult => {
	const additions: string[] = [];
	const current = planOptions ?? {};

	const duration = mergeScalarList(
		current.duration,
		proposed?.duration,
		"Duration",
		additions,
	);
	const visitsPerPeriod = mergeScalarList(
		current.visitsPerPeriod,
		proposed?.visitsPerPeriod,
		"Visits per period",
		additions,
	);
	const minHoursBetweenVisits = mergeScalarList(
		current.minHoursBetweenVisits,
		proposed?.minHoursBetweenVisits,
		"Min hours between visits",
		additions,
	);
	const behavior = mergeScalarList(
		current.behavior,
		proposed?.behavior,
		"Behaviour",
		additions,
	);

	const schedules: CmVisitScheduleOption[] = [
		...(current.cmVisitScheduleOptions ?? []),
	];
	for (const candidate of proposed?.visitSchedules ?? []) {
		const days = normaliseDays(candidate.days);
		if (!days) continue;
		const alreadyThere = schedules.some(
			(option) => normaliseDays(option.cmVisitScheduleOptionsDays) === days,
		);
		if (alreadyThere) continue;

		const visitsPerWeek = days.split(",").length;
		schedules.push({
			cmVisitScheduleOptions: nextOptionNumber(schedules),
			cmVisitScheduleOptionsDays: days,
			cmVisitScheduleOptionsDaysDisplay: candidate.display?.trim() || days,
			// Never taken from the assistant. Cut days decide whether anyone is
			// ever flagged for a missed visit, and the derivation already encodes
			// that the resolver never returns today.
			cutDays: deriveCutDays(visitsPerWeek),
		});
		additions.push(`Visit schedule: ${days}`);
	}

	return {
		options: {
			...current,
			duration,
			visitsPerPeriod,
			minHoursBetweenVisits,
			behavior,
			cmVisitScheduleOptions: schedules,
		},
		additions,
	};
};
